import { useState, useEffect, Dispatch, SetStateAction } from "react";
import { Button, FormGroup, Input } from "reactstrap";
import { useAtomValue } from "jotai";

import useCreateCloudAppFromFolder from "../../api/api-hooks/cloudApps/useCreateCloudAppFromFolder";
import useCreateCloudApp from "../../api/api-hooks/cloudApps/useCreateCloudApp";
import useModifyCloudApp from "../../api/api-hooks/cloudApps/useModifyCloudApp";
import BasicTableView from "../../views/supportal/CloudListView/BasicTableView";
import { CloudApp, GlobalApp } from "../../types/GlobalInterfaces";
import { FileInterface } from "../../types/RecordInterfaces";
import { workspaceIdAtom } from "../../atoms/WorkspaceAtoms";
import RequiredTitle from "../../elements/RequiredTitle";
import RichTextEditorV2 from "../CRM/RichTextEditorV2";
import IconButton from "../../elements/IconButton";
import AppSpinner from "../../elements/AppSpinner";
import MediaCreate from "../Records/MediaCreate";
import SingleFile from "../Inputs/SingleFile";
import { DrawerProps } from "./DrawerTypes";
import StrUtils from "../../utils/StrUtils";
import AppDrawer from "./AppDrawer";

export type CloudAppDrawerType =
  | "Create"
  | "CreateFromFolder"
  | "Edit"
  | undefined;

interface Props extends DrawerProps {
  folder?: any; // SidebarItem data for the folder
  existingCloudApp?: CloudApp | GlobalApp;
  type?: CloudAppDrawerType;
}

/**
 * Drawer used for creating a new Cloud App
 *
 * @param {boolean} open - whether the drawer is open or not
 * @param {function} handleClose - function to close the drawer
 * @param {string=} folderID - id of the folder to add the page to. Defaults to null.
 */
const CloudAppDrawer = ({
  open,
  onClose,
  folder,
  existingCloudApp,
  type,
}: Props) => {
  const { createCloudApp, createAppSubmitting } = useCreateCloudApp();
  const { createCloudAppFromFolder, createFromFolderSubmitting } =
    useCreateCloudAppFromFolder();
  const { modifyCloudApp, modifyAppSubmitting } = useModifyCloudApp();
  const workspaceID = useAtomValue(workspaceIdAtom);

  const [app, setApp] = useState<CloudApp | GlobalApp>({
    id: StrUtils.uuidv4(),
    Title: "",
    Description: { RichText: "", PlainText: "" },
    Summary: "",
    DefaultPage: "",
    Icon: undefined,
    CoverImage: undefined,
    KeyFeatures: [],
    Tags: [],
  });

  const [uploading, setUploading] = useState(false);

  const handleModalClose = () => {
    setUploading(false);
    onClose();
  };

  const getButtonText = () => {
    if (type === "CreateFromFolder" || type === "Create") {
      return "Save";
    }

    if (type === "Edit") {
      return "Update";
    }

    return "Save";
  };

  const createAppFromFolder = async () => {
    setUploading(true);
    if (!app) return;

    createCloudAppFromFolder({
      payload: {
        ...app,
        FolderID: folder.data.id,
        WorkspaceID: workspaceID,
      },
      onSuccess: handleModalClose,
    });
  };

  const createApp = async () => {
    setUploading(true);

    if (!app) return;

    createCloudApp({
      payload: {
        ...app,
      },
      onSuccess: handleModalClose,
      onError: handleModalClose,
    });
  };

  const modifyApp = async () => {
    if (!existingCloudApp) return;

    setUploading(true);

    if (!app) return;

    modifyCloudApp({
      payload: {
        ...app,
      },
      appID: existingCloudApp.id,
      onSuccess: handleModalClose,
    });
  };

  const handleSubmit = () => {
    if (type === "CreateFromFolder") {
      return createAppFromFolder();
    }

    if (type === "Create") {
      return createApp();
    }

    if (type === "Edit") {
      return modifyApp();
    }
  };

  useEffect(() => {
    if (existingCloudApp) {
      return setApp({
        Title: existingCloudApp.Title,
        Name: existingCloudApp.Name,
        Summary: existingCloudApp.Summary ?? "",
        Description:
          typeof existingCloudApp.Description === "string"
            ? {
                RichText: existingCloudApp.Description,
                PlainText: existingCloudApp.Description,
              }
            : typeof existingCloudApp.Description === "object"
            ? existingCloudApp.Description
            : undefined,
        Icon:
          typeof existingCloudApp.Icon === "string"
            ? { PublicURL: existingCloudApp.Icon }
            : typeof existingCloudApp.Icon === "object"
            ? existingCloudApp.Icon
            : undefined,
        // @ts-ignore
        Items: existingCloudApp?.Items,
        Images: existingCloudApp.Images,
        DefaultPage: existingCloudApp.DefaultPage
          ? existingCloudApp.DefaultPage
          : "",
        CoverImage: existingCloudApp.CoverImage,
        KeyFeatures: existingCloudApp.KeyFeatures,
        ReleaseNotes: existingCloudApp.ReleaseNotes,
        InstallationRequirements: existingCloudApp.InstallationRequirements,
        MSTags: existingCloudApp.MSTags,
      } as unknown as GlobalApp);
    }

    if (folder) {
      return setApp({
        Title: folder.data.Title,
        Name: folder.data.Title,
        Summary: folder.data.Summary ? folder.data.Summary : "",
        Description:
          typeof folder.data === "string"
            ? { RichText: folder.data, PlainText: folder.data }
            : typeof folder.data.Description === "object"
            ? folder.data.Description
            : undefined,
        Icon:
          typeof folder.Icon === "string"
            ? { PublicURL: folder.Icon }
            : typeof folder.Icon === "object"
            ? folder.Icon
            : undefined,
        Metadata: folder.Metadata,
        DefaultPage: folder.data.DefaultPage ? folder.data.DefaultPage : "",
        CoverImage: folder.data.CoverImage,
        KeyFeatures: folder.data.KeyFeatures,
        ReleaseNotes: folder.data.ReleaseNotes,
        InstallationRequirements: folder.data.InstallationRequirements,
        MSTags: folder.data.MSTags,
      } as CloudApp);
    }
  }, [existingCloudApp, folder]);

  const isSubmitting =
    createAppSubmitting || createFromFolderSubmitting || modifyAppSubmitting;

  return (
    <AppDrawer
      key={app?.id}
      open={open}
      onClose={handleModalClose}
      headerTitle={type === "Edit" ? "Editing App" : "New App"}
      headerOptions={
        <Button
          size="sm"
          color="primary"
          onClick={() => handleSubmit()}
          style={{ width: 100 }}
          disabled={!app?.Title || isSubmitting || uploading}
        >
          {isSubmitting || uploading ? (
            <AppSpinner size="sm" color="white" />
          ) : (
            getButtonText()
          )}
        </Button>
      }
      body={
        <>
          <FormGroup>
            <RequiredTitle title="Name" />
            <Input
              value={app?.Title}
              onChange={(e) =>
                setApp((prev) => ({
                  ...prev,
                  Name: e.target.value,
                  Title: e.target.value,
                }))
              }
            />
          </FormGroup>
          <FormGroup>
            <h5>Summary</h5>
            <Input
              placeholder="Summary"
              value={app?.Summary}
              onChange={(e) =>
                setApp((prev) => ({ ...prev, Summary: e.target.value }))
              }
              type="textarea"
            />
          </FormGroup>
          <FormGroup>
            <h5>Description</h5>
            <div className="tw:relative">
              <Button
                size="sm"
                className="bt-icon-only tw:absolute tw:right-0.5 tw:bottom-2.5"
              >
                <i className="mdi mdi-pencil"></i>
              </Button>
              <RichTextEditorV2
                isEditing
                value={app?.Description?.RichText || ""}
                onChange={(value) => {
                  setApp((prev) => ({
                    ...prev,
                    Description: {
                      RichText: value ? value : "",
                      PlainText: value
                        ? StrUtils.convertMarkupToPlaintext(value)
                        : "",
                    },
                  }));
                }}
              />
            </div>
          </FormGroup>
          <FormGroup>
            <h5>Cover Image</h5>
            <MediaCreate
              field={{
                Name: "Cover Image",
                APIName: "CoverImage",
                FieldType: "Image",
                Public: true,
              }}
              type="Image"
              existingValue={(app?.CoverImage as FileInterface) || null}
              inputSize="sm"
              onChange={(val) => {
                setApp((prev) => ({ ...prev, CoverImage: val ?? undefined }));
              }}
              disabled={false}
            />
          </FormGroup>
          <FormGroup>
            <h5>Images</h5>
            <SingleFile
              field={{
                Name: "Images",
                APIName: "Images",
                FieldType: "SingleFile",
                Public: true,
              }}
              value={app?.Images || []}
              inputSize="sm"
              onChange={(val) => {
                setApp((prev) => ({ ...prev, Images: val }));
              }}
              disabled={false}
              type="SingleFile"
            />
          </FormGroup>
          <FormGroup>
            <h5>Custom Icon</h5>
            <MediaCreate
              onChange={(val) => {
                setApp((prev) => ({ ...prev, Icon: val ?? undefined }));
              }}
              field={{
                Name: "Icon",
                APIName: "Icon",
                FieldType: "Image",
                Public: true,
              }}
              existingValue={app?.Icon || null}
              disabled={false}
              type="Image"
            />
          </FormGroup>
          <FormGroup>
            <ListInput
              property="ReleaseNotes"
              title="Release Notes"
              app={app}
              setApp={setApp}
            />
          </FormGroup>
          <FormGroup>
            <ListInput
              property="InstallationRequirements"
              title="Installation Requirements"
              app={app}
              setApp={setApp}
            />
          </FormGroup>
          <FormGroup>
            <ListInput
              property="MSTags"
              title="Tags"
              app={app}
              setApp={setApp}
            />
          </FormGroup>

          {app?.Items && Array.isArray(app?.Items) && (
            <FormGroup>
              <h5>Pages</h5>
              <BasicTableView
                items={app.Items}
                loading={false}
                title=""
                getItemTitle={(i) => i.Title}
                getItemSubtitle={() => ""}
                getItemIcon={() => {
                  return (
                    <i
                      className="mdi mdi-file-table-box-outline mr-3"
                      style={{ fontSize: 24 }}
                    />
                  );
                }}
                CustomRenderer={(i) => {
                  return (
                    <IconButton
                      icon={
                        app.DefaultPage === i.PageID ? "star" : "star-outline"
                      }
                      onClick={() => {
                        setApp((prev) => ({
                          ...prev,
                          DefaultPage: i.PageID,
                        }));
                      }}
                      color="warning"
                      tooltipText={"Set as default"}
                    />
                  );
                }}
              />
            </FormGroup>
          )}
        </>
      }
    />
  );
};

const ListInput = ({
  title,
  property,
  app,
  setApp,
}: {
  title: string;
  property: keyof CloudApp | keyof GlobalApp;
  app: CloudApp | GlobalApp;
  setApp: Dispatch<SetStateAction<CloudApp | GlobalApp>>;
}) => {
  const [value, setValue] = useState("");

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();

        if (!value) return;

        setApp((prev) => ({
          ...prev,
          [property]: [...((prev as any)?.[property] || []), { Title: value }],
        }));

        setValue("");
      }}
    >
      <h5>{title}</h5>
      <div className="tw:flex tw:items-center tw:mb-2">
        <Input
          value={value}
          bsSize="sm"
          className="tw:mr-2"
          onChange={(e) => setValue(e.target.value)}
        />
        <Button size="sm" color="primary" type="submit">
          Add
        </Button>
      </div>
      <BasicTableView
        items={(app as any)[property]}
        loading={false}
        title=""
        getItemTitle={(i: any) => i.Title}
        getItemSubtitle={() => ""}
        getItemIcon={() => {
          return (
            <i
              className="mdi mdi-file-table-box-outline mr-3"
              style={{ fontSize: 24 }}
            />
          );
        }}
        CustomRenderer={(i) => {
          return (
            <IconButton
              icon="delete"
              onClick={() => {
                setApp((prev) => {
                  return {
                    ...prev,
                    [property]: (prev as any)[property].filter(
                      (item: any) => item.Title !== i.Title
                    ),
                  };
                });
              }}
              color="danger"
              tooltipText={"Remove item"}
            />
          );
        }}
      />
    </form>
  );
};

export default CloudAppDrawer;
